import type { HeroSlide, SiteContent } from '../types'

export const MAX_HERO_SLIDES = 10
const imageMotions = ['cinematic-zoom', 'drift-left', 'drift-right', 'drift-up', 'drift-down', 'gentle-pullback', 'still'] as const
const imageSpeeds = ['gentle', 'slow', 'very-slow'] as const
const imageIntensities = ['subtle', 'balanced', 'cinematic'] as const
const imageFocusPoints = ['left', 'center', 'right'] as const
const imageVerticalFocusPoints = ['top', 'center', 'bottom'] as const
const textTones = ['auto', 'light', 'dark'] as const

export function normalizeHeroSlides(value: unknown): HeroSlide[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  const slides: HeroSlide[] = []

  for (const candidate of value) {
    if (!candidate || typeof candidate !== 'object' || slides.length >= MAX_HERO_SLIDES) continue
    const raw = candidate as Record<string, unknown>
    const desktopSrc = typeof raw.desktopSrc === 'string' ? raw.desktopSrc.trim() : ''
    const kind = raw.kind === 'video' ? 'video' : raw.kind === 'image' ? 'image' : null
    if (!desktopSrc || !kind) continue
    const fallbackId = `hero-slide-${slides.length + 1}`
    const requestedId = typeof raw.id === 'string' && /^[a-zA-Z0-9_-]{4,120}$/.test(raw.id)
      ? raw.id
      : fallbackId
    let id = requestedId
    while (seen.has(id)) id = `${requestedId}-${slides.length + 1}`
    seen.add(id)
    const motion = imageMotions.includes(raw.motion as typeof imageMotions[number])
      ? raw.motion as typeof imageMotions[number]
      : imageMotions[slides.length % 4]
    const motionSpeed = imageSpeeds.includes(raw.motionSpeed as typeof imageSpeeds[number])
      ? raw.motionSpeed as typeof imageSpeeds[number]
      : 'slow'
    const motionIntensity = imageIntensities.includes(raw.motionIntensity as typeof imageIntensities[number])
      ? raw.motionIntensity as typeof imageIntensities[number]
      : 'balanced'
    const focus = imageFocusPoints.includes(raw.focus as typeof imageFocusPoints[number])
      ? raw.focus as typeof imageFocusPoints[number]
      : 'center'
    const focusY = imageVerticalFocusPoints.includes(raw.focusY as typeof imageVerticalFocusPoints[number])
      ? raw.focusY as typeof imageVerticalFocusPoints[number]
      : 'center'
    const textTone = textTones.includes(raw.textTone as typeof textTones[number])
      ? raw.textTone as typeof textTones[number]
      : 'auto'
    slides.push({
      id,
      kind,
      desktopSrc,
      ...(typeof raw.mobileSrc === 'string' && raw.mobileSrc.trim() ? { mobileSrc: raw.mobileSrc.trim() } : {}),
      ...(typeof raw.label === 'string' && raw.label.trim() ? { label: raw.label.trim().slice(0, 80) } : {}),
      ...(kind === 'image' ? { motion, motionSpeed, motionIntensity, focus, focusY } : {}),
      textTone,
    })
  }

  return slides
}

export function heroSlidesWithLegacy(content: Pick<SiteContent, 'heroSlides' | 'heroDesktopVideo' | 'heroMobileVideo'>) {
  const slides = normalizeHeroSlides(content.heroSlides)
  if (slides.length) return slides
  const desktopSrc = content.heroDesktopVideo || content.heroMobileVideo
  if (!desktopSrc) return []
  return [{
    id: 'current-hero-video',
    kind: 'video' as const,
    desktopSrc,
    ...(content.heroMobileVideo ? { mobileSrc: content.heroMobileVideo } : {}),
    label: 'Current hero video',
  }]
}
